import React from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { cva } from "class-variance-authority";
import { TextRotate } from "./text-rotate";

// Page container variants
const pageContainerVariants = cva("w-full mx-auto relative", {
  variants: {
    spacing: {
      default: "py-16 md:py-24",
      sm: "py-8 md:py-12",
      md: "py-12 md:py-20",
      lg: "py-16 md:py-28",
      xl: "py-24 md:py-36",
      none: "py-0",
    },
  },
  defaultVariants: {
    spacing: "default",
  },
});

// Base page component with animations
const PageRoot = ({ children, className, ...props }) => {
  return (
    <motion.main
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.4 }}
      className={cn("min-h-[calc(100vh-80px)]", className)}
      {...props}
    >
      {children}
    </motion.main>
  );
};

// Page container component
const PageContainer = React.forwardRef(
  ({ children, className, size, spacing, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "container",
          pageContainerVariants({ size, spacing }),
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
PageContainer.displayName = "PageContainer";

// Page header component
const PageHeader = ({ children, className, centered, ...props }) => {
  return (
    <header
      className={cn(
        "mb-12 space-y-4",
        centered && "text-center mx-auto max-w-3xl",
        className
      )}
      {...props}
    >
      {children}
    </header>
  );
};

// Page title component
const PageTitle = ({ children, className, ...props }) => {
  return (
    <h1
      className={cn(
        "font-serif text-5xl md:text-7xl max-w-5xl tracking-tighter font-regular",
        className
      )}
      {...props}
    >
      <TextRotate
        texts={[children]}
        staggerFrom="first"
        staggerDuration={0.03}
        splitBy="characters"
      />
    </h1>
  );
};

// Page description component
const PageDescription = ({ children, className, ...props }) => {
  return (
    <p
      className={cn(
        "font-serif text-lg md:text-xl leading-relaxed tracking-tight text-muted-foreground max-w-2xl",
        className
      )}
      {...props}
    >
      {children}
    </p>
  );
};

// Page content component
const PageContent = ({ children, className, ...props }) => {
  return (
    <div className={cn("w-full", className)} {...props}>
      {children}
    </div>
  );
};

export {
  PageRoot,
  PageContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  PageContent,
};
